<template>
  <div :class="[$style.base, { [$style.isSmall]: $screen === 's' }]">
    <div :class="$style.nonPrintable">
      <Flex
        wrap
        align="end"
        justify="between"
      >
        <DateSelect
          :label="$t('global.actions.period')"
          @select="onDateSelect"
          v-test="'dateSelect'"
        />
        <BaseButton
          v-if="!isNativeApp"
          @click="onPrint"
          v-test="'printButton'"
        >
          {{ $t('global.print') }}
        </BaseButton>
      </Flex>
    </div>
    <div :class="[$style.upperWrapper, $style.nonPrintable]">
      <BaseSpinner v-show="$apollo.loading" />
      <div
        v-if="!$apollo.loading"
        :class="$style.upper"
      >
        <OverviewCard
          icon-name="credit-card"
          :title="orderKey('plural')"
          :value="overviewOrders"
          v-test="'salesCard'"
        />
        <OverviewCard
          icon-name="calendar"
          title="appointments"
          :value="overviewAppointments"
          v-test="'appointmentsCard'"
        />
        <OverviewCard
          icon-name="person"
          title="customers"
          :value="overviewCustomers"
          v-test="'customersCard'"
        />
      </div>
    </div>
    <DateHeader
      :start="startDate"
      :end="endDate"
    />
    <div
      v-show="!$apollo.loading"
      :class="$style.double"
    >
      <div :class="$style.column">
        <div :class="$style.card">
          <BaseCard
            :heading="$t('reports.totals.totals')"
            v-test="'totalsTable'"
          >
            <BaseTable
              :rows="
                totals.map((method: any) => ({
                  cells: [
                    $t(`reports.totals.${method.key}`),
                    {
                      value: filters.currency(method.value),
                      alignRight: true
                    }
                  ]
                }))
              "
              printCompact
            />
          </BaseCard>
        </div>
        <div
          v-if="!$apollo.loading"
          :class="$style.card"
        >
          <BaseCard
            :heading="$t('reports.totals.vat_rates')"
            v-test="'vatRatesTable'"
          >
            <BaseTable
              :headers="[
                $t('cashups.vat_rate'),
                { value: $t('reports.totals.base'), alignRight: true },
                { value: $t('global.vat'), alignRight: true }
              ]"
              :rows="
                totalsReport.vatRates.map((rate: any) => ({
                  cells: [
                    vatRateLabel(rate),
                    {
                      value: filters.currency(rate.revenue),
                      alignRight: true
                    },
                    {
                      value: filters.currency(rate.vat),
                      alignRight: true
                    }
                  ]
                }))
              "
              :footers="
                totalsReport.totals
                  ? [
                      $t('reports.totals.total_vat'),
                      {
                        value: '',
                        alignRight: true
                      },
                      {
                        value: filters.currency(totalsReport.totals.vat),
                        alignRight: true
                      }
                    ]
                  : []
              "
              printCompact
            />
          </BaseCard>
        </div>
        <div
          v-if="!$apollo.loading"
          :class="$style.card"
        >
          <BaseCard
            :heading="$t('reports.totals.vat_per_group')"
            v-test="'vatPerGroupTable'"
          >
            <BaseTable
              :rows="
                vatRatesByGroupRows.map((rate: any) => ({
                  cells: [
                    vatGroupRowLabel(rate),
                    {
                      value: filters.currency(rate.vat),
                      alignRight: true
                    }
                  ]
                }))
              "
              printCompact
            />
          </BaseCard>
        </div>
      </div>
      <div :class="$style.column">
        <div :class="$style.card">
          <PaymentMethods v-bind="{ paymentMethods, startDate, endDate }" />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { mapState } from 'pinia';
import { defineComponent } from 'vue';
import dayjs from '@/dayjs';
import filters from '@/filters';
import { orderKey } from '@/helpers/compliant-register';
import { underscore } from '@/helpers/formatting';
import { useCompanyStore } from '@/stores/company';
import { useLocationsStore } from '@/stores/locations';
import { useNativeAppStore } from '@/stores/native-app';
import { useTreatwellStore } from '@/stores/treatwell';
import unleash from '@/unleash';
import DateHeader from '../components/DateHeader.vue';
import DateSelect from '../components/DateSelect.vue';
import OverviewCard from './OverviewCard.vue';
import PaymentMethods from './PaymentMethods.vue';

export default defineComponent({
  components: {
    OverviewCard,
    DateSelect,
    DateHeader,
    PaymentMethods,
  },
  setup() {
    const { result } = useQuery(gql`
      query posTerminals {
        posTerminals {
          id
        }
      }
    `);

    const hasSalonizedPay = computed(
      () =>
        unleash.isEnabled('SalonizedPay') &&
        !!result.value?.posTerminals?.length,
    );
    provide('hasSalonizedPay', hasSalonizedPay);

    const { isNativeApp } = useNativeAppStore();

    return {
      isNativeApp,
      hasSalonizedPay,
      filters,
      orderKey,
    };
  },
  data() {
    return {
      totalsReport: {
        overview: {},
        paymentMethods: {},
        totals: {},
        vatRates: [],
        vatRatesByGroup: [],
      },
      totals: [],
      paymentMethods: [],
      startDate: dayjs().startOf('day').format(),
      endDate: dayjs().endOf('day').format(),
    };
  },
  apollo: {
    totalsReport: {
      query: gql`
        query totalsReport(
          $startDate: DateTime
          $endDate: DateTime
          $locationId: ID
          $dataScope: DataScope
        ) {
          totalsReport(
            startDate: $startDate
            endDate: $endDate
            locationId: $locationId
            dataScope: $dataScope
          ) {
            overview {
              orders
              appointments
              customers
            }
            totals {
              revenue
              revenueIncl
              revenueProducts
              revenueServices
              revenueTreatwellServices
              giftcardsSold
              prepaidCardsSold
              discount
              drawerTransactions
              vat
            }
            paymentMethods {
              pin
              cash
              creditcard
              bank
              cheque
              online
              coupon
              giftcard
              gifty
              prepaidCard
              outstanding
              treatwellOnlinePayment
              pos
              other
            }
            vatRates {
              percentage
              revenue
              vat
            }
            vatRatesByGroup {
              percentage
              group
              revenue
              vat
            }
          }
        }
      `,
      variables() {
        return {
          startDate: this.startDate,
          endDate: this.endDate,
          locationId: this.locationId,
          dataScope: this.dataScope,
        };
      },
      result() {
        this.setTotals();
        setTimeout(() => {
          this.setPaymentMethods();
        }, 0);
      },
    },
  },
  computed: {
    ...mapState(useCompanyStore, ['company']),
    ...mapState(useTreatwellStore, ['hasActiveVenue']),
    ...mapState(useLocationsStore, ['locationId', 'dataScope']),
    overviewOrders() {
      return this.totalsReport?.overview?.orders;
    },
    overviewAppointments() {
      return this.totalsReport?.overview?.appointments;
    },
    overviewCustomers() {
      return this.totalsReport?.overview?.customers;
    },
    hasOtherPaymentMethod() {
      return unleash.isEnabled('WithOtherPaymentMethod');
    },
    hasGifty() {
      return unleash.isEnabled('GiftyIntegration');
    },
    vatRatesByGroupRows() {
      if (this.totalsReport.vatRatesByGroup?.length) {
        return this.totalsReport.vatRatesByGroup;
      }
      // No sales in the period means the backend has no rates to group, so show a
      // zeroed-out row per known group instead of an empty-looking card.
      return ['service', 'product', 'giftcard', 'prepaid_card'].map(
        (group) => ({
          group,
          vat: 0,
          placeholder: true,
        }),
      );
    },
  },
  methods: {
    vatGroupRowLabel(rate) {
      const group = filters.capitalize(
        this.$t(`global.items.${rate.group}`, 2),
      );

      if (rate.placeholder) {
        return group;
      }

      const percentageRate =
        rate.percentage === null || rate.percentage === undefined
          ? this.$t('reports.vat_overview.no_vat')
          : `${rate.percentage}%`;

      return `${group} (${percentageRate})`;
    },
    vatRateLabel(rate) {
      return rate.percentage === null || rate.percentage === undefined
        ? this.$t('reports.vat_overview.no_vat')
        : `${rate.percentage}%`;
    },
    setTotals() {
      const keys = [
        'revenue',
        'revenueIncl',
        'revenueProducts',
        'revenueServices',
        ...(this.hasActiveVenue ? ['revenueTreatwellServices'] : []),
        'giftcardsSold',
        'prepaidCardsSold',
        'discount',
        'drawerTransactions',
      ];
      return (this.totals = keys.map((key) => ({
        key: underscore(key),
        value: this.totalsReport?.totals?.[key] || 0,
      })));
    },
    setPaymentMethods() {
      const keys = [
        ...(this.hasSalonizedPay ? ['pos'] : []),
        'pin',
        'cash',
        'creditcard',
        'bank',
        'cheque',
        'online',
        'coupon',
        'giftcard',
        ...(this.hasGifty ? ['gifty'] : []),
        'prepaidCard',
        ...(this.hasActiveVenue ? ['treatwellOnlinePayment'] : []),
        ...(this.hasOtherPaymentMethod ? ['other'] : []),
        'outstanding',
      ];
      return (this.paymentMethods = keys.map((key) => ({
        key: underscore(key),
        value: this.totalsReport?.paymentMethods?.[key] || 0,
      })));
    },

    onDateSelect(dates) {
      this.startDate = dates.startDate;
      this.endDate = dates.endDate;
    },
    onPrint() {
      window.print();
    },
  },
});
</script>
<style>
@media print {
  .main-wrapper {
    padding-top: 0 !important;
  }
}
</style>
<style
  lang="scss"
  module
>
.base {
  min-height: 100%;
}

.inner {
  width: 100%;
}

.column {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: $spacing;
}

.double {
  display: flex;
  gap: $spacing;
  margin-top: $spacing;
  margin-bottom: $spacing;

  .isSmall & {
    flex-direction: column;
  }
}

.upperWrapper {
  position: relative;
  margin-top: $spacing;
}

.upper {
  display: flex;
  justify-content: space-between;
  gap: $spacing;

  .isSmall & {
    flex-direction: column;
  }

  & > * {
    flex: 1;
  }
}

.under {
  margin: 0 $spacing * 0.5 0;
  padding-bottom: $spacing * 0.5;
}

.select {
  width: 200px;
  margin-right: $spacing;
}

@media print {
  .nonPrintable {
    display: none;
  }
  .card {
    padding: 0 !important;
  }
}
</style>
